import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { api } from '../../api/client';
import { Wordmark } from '../../layout/AppShell';
import { Alert, PageLoading } from '../../ui/feedback';
import { KeyValue, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

interface Preview {
  emailHint: string;
  role: 'user' | 'admin';
  state: 'pending' | 'redeemed' | 'revoked' | 'expired';
  expiresAt: string;
  projects: { key: string; name: string }[];
}

const ERRORS: Record<string, string> = {
  invitation_mismatch:
    'This invitation belongs to a different email address, or your identity provider did not verify your email. Sign in with the invited account.',
  invitation_expired: 'This invitation has expired. Ask an admin for a new one.',
  invitation_revoked: 'This invitation was revoked. Ask an admin for a new one.',
  invitation_redeemed: 'This invitation was already used. Sign in normally, or ask an admin for help.',
  invitation_invalid: 'This invitation link is not valid.',
};

/** IDN-02: review role, memberships and expiry; redemption requires OIDC sign-in with the invited email. */
export function AcceptInvitationPage() {
  usePageTitle('Accept invitation');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const error = params.get('error');
  const q = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<Preview>(`/invitations/preview?token=${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
  });
  const usable = q.data?.state === 'pending';
  return (
    <div className="auth-layout">
      <div className="auth-card wide">
        <Wordmark size={28} />
        <div className="stack sm">
          <h1 className="page-title">Accept invitation</h1>
          {q.data && (
            <p className="muted">
              You were invited as <strong className="strong">{q.data.emailHint}</strong>.
            </p>
          )}
        </div>
        {error && <Alert tone="error">{ERRORS[error] ?? 'The invitation could not be redeemed.'}</Alert>}
        {!token && <Alert tone="error">This invitation link is incomplete.</Alert>}
        {q.isPending && token && <PageLoading />}
        {q.error && <Alert tone="error">{ERRORS.invitation_invalid}</Alert>}
        {q.data && (
          <>
            {q.data.state !== 'pending' && (
              <Alert tone="warning">{ERRORS[`invitation_${q.data.state}`]}</Alert>
            )}
            <div className="well">
              <KeyValue
                items={[
                  ['Role', q.data.role === 'admin' ? 'Admin' : 'Member'],
                  [
                    'Projects',
                    q.data.projects.length ? (
                      <div className="stack" style={{ gap: 4 }}>
                        {q.data.projects.map((p) => (
                          <span key={p.key}>
                            <span className="mono muted">{p.key}</span> {p.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'None yet'
                    ),
                  ],
                  ['Expires', <Timestamp key="e" value={q.data.expiresAt} />],
                ]}
              />
            </div>
            <a
              className={`btn btn-primary block${usable ? '' : ' disabled'}`}
              aria-disabled={!usable}
              href={
                usable
                  ? `/api/v1/auth/login?invitation=${encodeURIComponent(token)}&returnTo=/projects`
                  : undefined
              }
            >
              Sign in to accept
            </a>
            <p className="small muted">
              You'll sign in with your organization's identity provider. Your verified email must match the
              invitation.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
